import { Box, Tooltip, Typography } from '@mui/material';
import {
    alpha,
    darken,
    getContrastRatio,
    lighten,
    useTheme,
} from '@mui/material/styles';
import type { TraderLabel } from '../api/searchTypes';

export type TagState = 'off' | 'include' | 'exclude';

export interface TraderLabelStyle {
    color?: string;
    // `color` made readable as text on the paper it was worked out for.
    textColor?: string;
    description: string;
}

const HEX_COLOR = /^#[0-9a-f]{3,8}$/i;

// schema.trader_labels by name, for the filter panel and the trader rows.
// The readable text colour is worked out here, once per label for the given
// paper, so a chip does not repeat the contrast search on every render.
export function traderLabelStyles(
    labels: readonly TraderLabel[] | undefined,
    paper: string,
    mode: 'light' | 'dark'
): Map<string, TraderLabelStyle> {
    const map = new Map<string, TraderLabelStyle>();
    for (const label of labels ?? []) {
        // alpha() throws on anything but a plain colour; drop such a
        // colour and keep the description.
        const color = HEX_COLOR.test(label.color) ? label.color : undefined;
        const textColor = color
            ? readableLabelColor(color, paper, mode)
            : undefined;
        map.set(label.tag, {
            color,
            textColor,
            description: label.description,
        });
    }
    return map;
}

// Some label colours (Insider #ffd4a6, Test #aefba2) are too light to read
// as text on the light paper. They stay as they are for the border and the
// tint; the text gets the smallest darken (lighten in dark mode) in steps of
// 0.1 that reaches 3:1 against the paper.
const MIN_TEXT_CONTRAST = 3;

export function readableLabelColor(
    color: string,
    paper: string,
    mode: 'light' | 'dark'
): string {
    if (getContrastRatio(color, paper) >= MIN_TEXT_CONTRAST) return color;
    const shift = mode === 'dark' ? lighten : darken;
    for (let step = 1; step < 10; step++) {
        const shifted = shift(color, step / 10);
        if (getContrastRatio(shifted, paper) >= MIN_TEXT_CONTRAST)
            return shifted;
    }
    return shift(color, 1);
}

export interface TagChipProps {
    label: string;
    state?: TagState;
    strong?: boolean;
    // A trader label's own colour and description (schema.trader_labels),
    // and its text colour from traderLabelStyles. Without `textColor` the
    // chip works it out from `color`.
    color?: string;
    textColor?: string;
    description?: string;
    onInclude?: (label: string) => void;
    onExclude?: (label: string) => void;
}

const CLICK_HINT = 'Click to keep, alt-click to drop';

export function TagChip({
    label,
    state = 'off',
    strong = false,
    color: labelColor,
    textColor,
    description,
    onInclude,
    onExclude,
}: TagChipProps) {
    const theme = useTheme();
    const clickable = Boolean(onInclude || onExclude);
    const includeColor = labelColor ?? theme.palette.primary.main;
    const excludeColor = '#A32D2D';
    const labelText =
        textColor ??
        (labelColor
            ? readableLabelColor(
                  labelColor,
                  theme.palette.background.paper,
                  theme.palette.mode
              )
            : undefined);

    const border =
        state === 'include'
            ? alpha(includeColor, 0.7)
            : state === 'exclude'
              ? alpha(excludeColor, 0.7)
              : labelColor
                ? alpha(labelColor, 0.5)
                : alpha(theme.palette.text.primary, strong ? 0.3 : 0.18);
    const background =
        state === 'include'
            ? alpha(includeColor, 0.16)
            : state === 'exclude'
              ? alpha(excludeColor, 0.16)
              : 'transparent';
    const color =
        state === 'include'
            ? labelText ?? includeColor
            : state === 'exclude'
              ? excludeColor
              : labelText
                ? labelText
                : strong
                  ? theme.palette.text.secondary
                  : theme.palette.text.disabled;

    const hint = clickable ? CLICK_HINT : '';
    const title = description
        ? `${description}${hint ? ` ${hint}.` : ''}`
        : hint;

    return (
        <Tooltip title={title} placement="top" disableInteractive>
            <Box
                onClick={event => {
                    if (!clickable) return;
                    event.stopPropagation();
                    if (event.altKey || event.shiftKey) onExclude?.(label);
                    else onInclude?.(label);
                }}
                sx={{
                    px: 0.5,
                    borderRadius: '3px',
                    border: '1px solid',
                    borderColor: border,
                    bgcolor: background,
                    flexShrink: 0,
                    cursor: clickable ? 'pointer' : 'default',
                    '&:hover': clickable
                        ? { borderColor: alpha(includeColor, 0.6) }
                        : undefined,
                }}
            >
                <Typography
                    sx={{
                        fontSize: '9px',
                        fontWeight: state === 'off' ? 500 : 700,
                        color,
                        whiteSpace: 'nowrap',
                        lineHeight: 1.6,
                    }}
                >
                    {state === 'exclude' ? `−${label}` : label}
                </Typography>
            </Box>
        </Tooltip>
    );
}
